import React, { FC, ReactNode, useContext, useEffect, useState } from 'react'

import { ChevronDownIcon, PlusIcon } from 'lucide-react'

import { ClassesSkeleton } from '../../empty/LineItemsEmpty/LineItemsEmpty'
import { PaymentAllActions } from '../../PaymentAllActions/PaymentAllActions'
import { PaymentActions } from '../../PaymentActions/PaymentActions'
import { PaymentStatus } from '../../../../../../pages/receipt/components/PaymentStatus/PaymentStatus'
import { PriceItem } from '../../../../../common/price/PriceItem'
import { Button } from '../../../../../ui/button'

import { cn } from '../../../../../../lib/utils'
import { getFloatPrice } from '../../../../../../helpers/price'
import { getRegistrationEventDataInDb } from '../../../../../../helpers/registration-on-event'
import {
  getCurrency,
  getPaymentMethodsFormatted,
  getTotalPrices,
  isPaymentPending,
  isRefundPending,
  ITotalPrices,
} from '../../../../../../helpers/payment'
import { useEventRiderTeamMembers } from '../../../../../../hooks/useEventRiderTeamMembers'
import { usePaymentActions } from '../../../../../../hooks/usePaymentActions'
import useToasterHelper from '../../../../../../helpers/ToasterHelper'

import { useAppSelector } from '../../../../../../store/hooks'
import { selectProfileData } from '../../../../../../store/user/userSlice'

import FirestoreService from '../../../../../../services/firestoreService'

import { IRegistrationTicketInterface } from '../../../../../../models/registration-tickets/registrationTicket.interface'
import { IEventReviewPublish } from '../../../../../../models/event-review-publish/event-review-publish.interface'
import { IPaymentsCards, IUserInterface } from '../../../../../../models/users/user.interface'
import { IRiderTeamMemberInterface } from '../../../../../../models/rider-team-member/riderTeamMember.interface'
import { PAYMENT_STATUS } from '../../../../../../types/payment'

import { MESSAGES_CONST } from '../../../../../../const/messages-const'
import { CONST } from '../../../../../../const/const'
import { MODAL_CONSTS } from '../../../../../../const/modal-const'
import { HandleModalContext } from '../../../../../../layout/mainlayout/MainLayout'
import { REGISTRAR_TABS } from '../../../RegistrarInfoModal/RegistrarInfoModal'
import { RIDER_TABS } from '../../../RiderInfoModal/RiderInfoModal'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

interface TicketsTabProps {
  isOpenedDefault?: boolean
  type?: 'overview' | 'registrar'
  className?: string
  isCollapsible?: boolean
  registrarId: string | null
  registrationId: string
  eventId: string
  publishedEvent?: IEventReviewPublish | null
  user: IUserInterface | null
  organizer: IUserInterface | null
  onActionClick?: () => void
  actionIcon?: ReactNode
}
export const TicketsTab: FC<TicketsTabProps> = ({
  isOpenedDefault,
  type,
  className,
  isCollapsible,
  registrarId,
  registrationId,
  eventId,
  publishedEvent,
  user,
  organizer,
  onActionClick,
  actionIcon,
}) => {
  const handleModalContext = useContext(HandleModalContext)
  const toastFunctions = useToasterHelper()

  const { eventTeamMembers } = useEventRiderTeamMembers(eventId || '')
  const { onScratch, onRefund, onPayByCard, onMarkPaid, onRecipientClick, onUndoMarkMaid } =
    usePaymentActions()

  const profileData = useAppSelector(selectProfileData)

  const [loading, setLoading] = useState(true)
  const [isOpen, setIsOpen] = useState(!isCollapsible || isOpenedDefault)
  const [tickets, setTickets] = useState<IRegistrationTicketInterface[]>([])
  const [total, setTotal] = useState<ITotalPrices>()
  const [showAdjustField, setShowAdjustField] = useState<number | null>(null)
  const [showRefundField, setShowRefundField] = useState<number | null>(null)
  const [filteredTickets, setFilteredTickets] = useState<IRegistrationTicketInterface[]>([])
  const [paymentsCards, setPaymentsCards] = useState<IPaymentsCards>({ CARDS: [], ACH: [] })

  useEffect(() => {
    let filtered

    const sorted = tickets.sort(
      (a, b) => new Date(b.created as Date).getTime() - new Date(a.created as Date).getTime()
    )

    if (type === 'overview') {
      filtered = sorted.filter((ticket) => {
        return (
          isPaymentPending(ticket.isScratched, ticket.paymentStatus) ||
          isRefundPending(
            ticket.isScratched,
            ticket.refundAmountAdded,
            ticket.paymentStatus,
            ticket.paymentType
          )
        )
      })
    } else {
      filtered = sorted
    }
    setFilteredTickets(filtered)
  }, [tickets, type])

  useEffect(() => {
    const getData = async () => {
      if (!user?.id || !publishedEvent?.id) return

      try {
        setLoading(true)

        const { registrationsTickets } = await getRegistrationEventDataInDb({
          registrationId,
          ownerId: user.id,
          type: ['tickets'],
        })

        const { totalTickets } = getTotalPrices({
          registrationsEntries: [],
          registrationsFees: [],
          registrationsTickets: registrationsTickets,
          isChargeSalesTax: publishedEvent?.EventPaymentSettings?.chargeSalesTax ?? false,
        })

        let userEmail
        // If it is an organizer, we show the riders' cards; if it is the registrar, we show the registrar's cards.
        if (organizer?.id === profileData.id) userEmail = user.userEmail
        else userEmail = profileData.userEmail

        const payments = await getPaymentMethodsFormatted(userEmail, organizer?.userStripeAccountId)

        setTickets(registrationsTickets)
        setTotal(totalTickets)
        setPaymentsCards(payments)
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setLoading(false)
      }
    }
    getData().then()
  }, [
    organizer?.id,
    organizer?.userStripeAccountId,
    profileData.id,
    profileData.userEmail,
    publishedEvent,
    registrationId,
    user?.id,
    user?.userEmail,
  ])

  const refundPending = (items: IRegistrationTicketInterface[]) => {
    return (
      items?.filter((ticket) =>
        isRefundPending(
          ticket.isScratched,
          ticket.refundAmountAdded,
          ticket.paymentStatus,
          ticket.paymentType
        )
      )?.length || 0
    )
  }

  const paymentPending = filteredTickets?.filter((ticket) =>
    isPaymentPending(ticket.isScratched, ticket.paymentStatus)
  )

  const haveScratched = filteredTickets?.filter((ticket) => !ticket.isScratched) ?? []

  const haveNotPaid =
    filteredTickets?.filter((ticket) => ticket.paymentStatus === PAYMENT_STATUS.DEFAULT) ?? []

  const onSaveAdjust = async (adjustItem: IRegistrationTicketInterface, adjustedValue: number) => {
    try {
      await FirestoreService.updateItem(COLLECTIONS.REGISTRATION_TICKET.NAME, adjustItem.id, {
        ticketPrice: adjustedValue,
        originalTicketPrice: adjustItem.originalTicketPrice ?? adjustItem.ticketPrice,
      })

      const { registrationsTickets } = await getRegistrationEventDataInDb({
        registrationId,
        ownerId: user?.id,
        type: ['tickets'],
      })
      setTickets(registrationsTickets)

      setShowAdjustField(null)

      toastFunctions.success({
        message: `Adjusted successfully`,
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onSaveRefund = async (
    refundItems: IRegistrationTicketInterface[],
    refundValue?: number
  ) => {
    if (organizer?.id && user?.id) {
      const response = await onRefund({
        refundRegistrationsByDayItems: [],
        refundFeesItems: [],
        refundTicketsItems: refundItems,
        registrationDocId: registrationId,
        publishedEvent: publishedEvent,
        ownerId: user?.id,
        refundValue: refundValue,
      })
      setShowRefundField(null)
      if (response?.registrationsTicketsUpdated) setTickets(response.registrationsTicketsUpdated)
    }
  }

  const onSaveAssign = async (
    assignItem: IRegistrationTicketInterface,
    teamMember: IRiderTeamMemberInterface
  ) => {
    try {
      await FirestoreService.updateItem(COLLECTIONS.REGISTRATION_TICKET.NAME, assignItem.id, {
        recipientId: teamMember?.teamMemberId || null,
        recipientName: teamMember?.teamMemberName || null,
        recipientProfilePicture: teamMember?.teamMemberProfilePicture || null,
      })

      const { registrationsTickets } = await getRegistrationEventDataInDb({
        registrationId,
        ownerId: user?.id,
        type: ['tickets'],
      })
      setTickets(registrationsTickets)

      toastFunctions.success({
        message: `Assigned successfully`,
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onDeleteItem = async (id: string) => {
    try {
      await FirestoreService.deleteItem(COLLECTIONS.REGISTRATION_TICKET.NAME, id)

      const { registrationsTickets } = await getRegistrationEventDataInDb({
        registrationId,
        ownerId: user?.id,
        type: ['tickets'],
      })

      setTickets(registrationsTickets)

      toastFunctions.success({
        message: 'Ticket Deleted Successfully',
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onAddTickets = () => {
    handleModalContext?.handleModal(true, MODAL_CONSTS.ADD_TICKETS, {
      ...handleModalContext?.dataToPassOn,
      eventId: eventId,
      registrationDocId: registrationId,
      publishedEvent: publishedEvent,
      user: user,
      onSave: (newTickets: IRegistrationTicketInterface[]) => {
        setTickets([...tickets, ...newTickets])
      },
    })
  }

  const currency = getCurrency(publishedEvent?.EventPaymentSettings?.currency)

  const onUpdate = (registrationsTicketsUpdated: IRegistrationTicketInterface[]) => {
    const updated = tickets.map((ticket) => {
      const current = registrationsTicketsUpdated?.find((item) => item.id === ticket.id)
      if (current?.id) {
        return { ...ticket, ...current }
      } else {
        return ticket
      }
    })
    setTickets(updated)
  }

  if (loading) return <ClassesSkeleton />

  return (
    <div className={cn('rounded-lg border border-black/10 px-7 py-3', className)}>
      <div className="flex gap-2 flex-wrap items-center justify-between">
        <div>
          <p className="text-base font-medium">Tickets</p>
          {type === 'overview' ? (
            <p className="text-xs text-black/50">
              {filteredTickets?.[0] ? `${filteredTickets.length} issues` : 'No issues'}
            </p>
          ) : (
            <p className="text-xs text-black/50">
              {filteredTickets?.length || 0} items,{' '}
              {getFloatPrice(`${currency}${(total?.paid || 0) / 100}`)} total paid
            </p>
          )}
        </div>
        <div className="text-right flex gap-3 items-center justify-end ml-auto">
          {((type === 'registrar' && !filteredTickets?.[0]) ||
            (type !== 'overview' && !filteredTickets?.[0])) && (
            <Button
              variant="link"
              type="button"
              className="rounded-full  w-[30px] h-[30px] text-black bg-black/5 hover:opacity-70 transition-all"
              onClick={onAddTickets}
            >
              <PlusIcon className="w-4" />
            </Button>
          )}
          {type === 'overview' ? (
            <Button
              variant="link"
              type="button"
              className="rounded-full p-0 hover:opacity-70"
              onClick={onActionClick}
            >
              {actionIcon}
            </Button>
          ) : (
            filteredTickets?.[0] && (
              <>
                <div className="flex gap-2 items-center justify-end flex-wrap">
                  {refundPending(filteredTickets) > 0 && (
                    <div className="flex items-center text-sm text-PurpleThemeColor bg-PurpleThemeColor/5 px-3 py-1 rounded-full">
                      {refundPending(filteredTickets)} refund pending
                    </div>
                  )}
                  {paymentPending && paymentPending.length > 0 && (
                    <div className="flex items-center text-sm text-SeabiscuitRedThemeColor bg-SeabiscuitRedThemeColor/5 px-3 py-1 rounded-full">
                      {paymentPending.length} payment pending
                    </div>
                  )}
                </div>
                <PaymentAllActions
                  type="ticket"
                  haveScratched={haveScratched?.length > 0}
                  isRefundPending={refundPending(filteredTickets) > 0}
                  haveNotPaid={haveNotPaid.length > 0}
                  paymentsCards={paymentsCards}
                  organizer={organizer}
                  setPaymentsCards={setPaymentsCards}
                  user={user}
                  eventReviewPublish={publishedEvent}
                  onScratch={async () => {
                    const response = await onScratch({
                      registrationDocId: registrationId,
                      publishedEvent: publishedEvent,
                      user: user,
                      registrationsByDayScratchedItems: [],
                      feesScratchedItems: [],
                      ticketsScratchedItems: filteredTickets.filter(
                        (ticket) => !ticket.isScratched
                      ),
                      isScratched: false,
                    })
                    if (response?.registrationsTicketsUpdated)
                      onUpdate(response.registrationsTicketsUpdated)
                  }}
                  onRefund={async () => {
                    const items = filteredTickets.filter(
                      (ticket) =>
                        ticket.isScratched &&
                        !ticket.refundAmountAdded &&
                        ticket.paymentStatus === PAYMENT_STATUS.PAID
                    )
                    await onSaveRefund(items)
                  }}
                  onMarkPaidAction={async (values) => {
                    if (user) {
                      const response = await onMarkPaid({
                        markPaidRegistrationsByDayItems: [],
                        markPaidFeesItems: [],
                        markPaidTicketsItems: filteredTickets.filter(
                          (ticket) => !ticket.paymentStatus && !ticket.isScratched
                        ),
                        paymentType: values.type,
                        checkMemo: values.checkMemo,
                        checkNumber: values.checkNumber,
                        registrationDocId: registrationId,
                        user: user,
                        organizerId: organizer?.id || null,
                      })
                      if (response?.registrationsTicketsUpdated)
                        onUpdate(response.registrationsTicketsUpdated)
                    }
                  }}
                  onPayByCard={async (card) => {
                    if (user && registrarId) {
                      const response = await onPayByCard({
                        registrationsByDayItems: [],
                        feeItems: [],
                        ticketItems: filteredTickets.filter(
                          (ticket) => !ticket.paymentStatus && !ticket.isScratched
                        ),
                        card: card,
                        registrationDocId: registrationId,
                        user: user,
                        publishedEvent: publishedEvent,
                      })

                      if (response?.registrationsTicketsUpdated) {
                        onUpdate(response?.registrationsTicketsUpdated)
                      }
                    }
                  }}
                  onAddNew={onAddTickets}
                />
              </>
            )
          )}
          {isCollapsible && (
            <Button
              variant="link"
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                'rounded-full duration-200 w-[30px] h-[30px] flex items-center justify-center shrink-0 hover:opacity-70 transition-all',
                isOpen
                  ? 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rotate-180'
                  : 'text-black bg-black/5'
              )}
            >
              <ChevronDownIcon className="size-4" />
            </Button>
          )}
        </div>
      </div>
      {isOpen && (
        <div
          className={cn(
            (!isCollapsible || isOpen) && filteredTickets?.[0] && 'mt-3 border-t border-t-black/10'
          )}
        >
          {filteredTickets?.[0]
            ? filteredTickets.map((ticket, index) => {
                return (
                  <div
                    key={index}
                    className={cn(
                      'flex gap-2 flex-wrap items-center justify-between py-4',
                      filteredTickets.length > index + 1 && 'border-b border-b-black/10'
                    )}
                  >
                    <div>
                      <p className="text-sm flex items-center gap-2 flex-wrap mb-1">
                        <span className={cn(ticket.isScratched && 'line-through')}>
                          {ticket.ticketTitle}
                        </span>
                      </p>
                      <div className="text-xs text-black/50">
                        {ticket.selectedUnitsCount > 0 && `${ticket.selectedUnitsCount} units,`}{' '}
                        <PriceItem
                          isRefunded={ticket.refundAmountAdded}
                          amountRefunded={ticket.amountRefunded}
                          price={ticket.ticketPrice || 0}
                          originalPrice={ticket.originalTicketPrice || 0}
                          showAdjustField={showAdjustField === index}
                          onCancelAdjust={() => setShowAdjustField(null)}
                          onSaveAdjust={(value) => onSaveAdjust(ticket, value)}
                          showRefundField={showRefundField === index}
                          onCancelRefund={() => setShowRefundField(null)}
                          onSaveRefund={(value) => onSaveRefund([ticket], value)}
                          currency={currency}
                        />
                      </div>
                    </div>
                    <div className="text-right ml-auto">
                      {type !== 'overview' && (
                        <PaymentActions
                          isRefundPending={refundPending([ticket]) > 0}
                          eventTeamMembers={eventTeamMembers}
                          paymentStatus={ticket.paymentStatus}
                          paymentType={ticket.paymentType}
                          isScratched={ticket.isScratched ?? false}
                          isRefunded={ticket.refundAmountAdded ?? false}
                          paymentsCards={paymentsCards}
                          organizer={organizer}
                          user={user}
                          eventReviewPublish={publishedEvent}
                          setPaymentsCards={setPaymentsCards}
                          onScratch={async (type) => {
                            const response = await onScratch({
                              registrationDocId: registrationId,
                              publishedEvent: publishedEvent,
                              user: user,
                              registrationsByDayScratchedItems: [],
                              feesScratchedItems: [],
                              ticketsScratchedItems: [ticket],
                              isScratched: type,
                            })
                            if (response?.registrationsTicketsUpdated)
                              onUpdate(response.registrationsTicketsUpdated)
                          }}
                          onMarkPaidAction={async (values) => {
                            if (user) {
                              const response = await onMarkPaid({
                                markPaidRegistrationsByDayItems: [],
                                markPaidFeesItems: [],
                                markPaidTicketsItems: [ticket],
                                paymentType: values.type,
                                checkMemo: values.checkMemo,
                                checkNumber: values.checkNumber,
                                registrationDocId: registrationId,
                                user: user,
                                organizerId: organizer?.id || null,
                              })
                              if (response?.registrationsTicketsUpdated) {
                                onUpdate(response?.registrationsTicketsUpdated)
                              }
                            }
                          }}
                          onAdjust={() => setShowAdjustField(index)}
                          onRefund={() => setShowRefundField(index)}
                          onSaveAssign={(teamMember) => onSaveAssign(ticket, teamMember)}
                          onPayByCard={async (card) => {
                            if (user && registrarId) {
                              const response = await onPayByCard({
                                registrationsByDayItems: [],
                                feeItems: [],
                                ticketItems: [ticket],
                                card: card,
                                registrationDocId: registrationId,
                                user: user,
                                publishedEvent: publishedEvent,
                              })

                              if (response?.registrationsTicketsUpdated) {
                                onUpdate(response?.registrationsTicketsUpdated)
                              }
                            }
                          }}
                          onDelete={async () => await onDeleteItem(ticket.id || '')}
                          registrationDocId={registrationId}
                        />
                      )}
                      <div
                        className={cn(
                          'flex items-center text-sm',
                          isRefundPending(
                            ticket.isScratched,
                            ticket.refundAmountAdded,
                            ticket.paymentStatus,
                            ticket.paymentType
                          )
                            ? 'text-PurpleThemeColor'
                            : isPaymentPending(ticket.isScratched, ticket.paymentStatus)
                              ? 'text-SeabiscuitRedThemeColor'
                              : ' text-black/50',
                          type === 'overview' ? 'rounded-full py-1 px-3' : 'bg-transparent'
                        )}
                      >
                        <PaymentStatus
                          isAdjusted={!!ticket.originalTicketPrice}
                          isRefunded={ticket.refundAmountAdded || false}
                          isScratched={ticket.isScratched || false}
                          paymentStatus={ticket.paymentStatus}
                          scratchedDate={ticket.scratchedDate || null}
                          refundedDate={ticket.refundedDate || null}
                          paidDate={ticket.paidDate || null}
                          paymentType={ticket.paymentType}
                          ownerId={ticket.ownerId || null}
                          ownerName={ticket.ownerName || null}
                          recipientId={ticket.recipientId || null}
                          recipientName={ticket.recipientName || null}
                          userId={user?.id}
                          onRecipientClick={(value) =>
                            onRecipientClick({
                              eventId,
                              registrarId,
                              registrationId,
                              defaultActiveTab: REGISTRAR_TABS.TEAM.value,
                              defaultOpenedPrimaryAccordion: value,
                              defaultOpenedSecondaryAccordion: RIDER_TABS.TICKETS
                                .value as 'tickets',
                            })
                          }
                          onUndoMarkMaid={async () => {
                            if (ticket.id && user) {
                              const response = await onUndoMarkMaid({
                                eventId: eventId,
                                registrationId: registrationId,
                                paymentType: ticket.paymentType,
                                registrationsByDay: [],
                                fees: [],
                                tickets: [ticket],
                                user: user,
                                publishedEvent: publishedEvent,
                              })

                              if (response?.registrationsTicketsUpdated)
                                onUpdate(response.registrationsTicketsUpdated)
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )
              })
            : type === 'registrar' && (
                <div className="pt-4 mt-4 border-t border-t-black/10">
                  <div className=" text-black/50 text-xs bg-black/[2%] h-[112px] rounded-lg flex items-center justify-center p-4">
                    No tickets have been added to this entry
                  </div>
                </div>
              )}
        </div>
      )}
    </div>
  )
}
